import { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { TableHeader } from "../../components/datatable/TableHeader";
import { FormSkeleton } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import { AppContext } from "../../context/index";
import { Column } from 'iuvity-ui-components/dist/column'
import { AccessButton, Accordion, Calendar } from "iuvity-ui-components";
import { Actions } from "../datatable/Actions";
import { getRolesByTenant } from "../../services/console/roles";

const formBlankValues = {
  objective: '',
  segment: '',
  environment: '',
};

interface ManagementRolesProps {
  data?: any;
  adminSelection?: any;
  setAdminSelection?: any;
  isButtonAppRoles?: boolean;
  isButtonAdminRoles?: boolean;
  userRoleData?: any;
  isUpdate?: boolean;
  onSelectRole?: any;
}

export const ManagementRolesComponent: React.FC<ManagementRolesProps> = ({
  data,
  adminSelection,
  setAdminSelection,
  isButtonAppRoles,
  isButtonAdminRoles,
  userRoleData,
  isUpdate = false,
  onSelectRole
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, selectedManagementRoles, tenantParentName, selectedTenantId, tenantId },
    setAppState
  } = useContext(AppContext);

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const dataTenantsList = data;

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "select",
          header: "",
        },
        {
          field: "name",
          header: t("Users.rolNameLabel"),
        },
        {
          field: "roleTypeId",
          header: t("Users.rolTypeIdLabel"),
        },
        {
          field: "expeditionDate",
          header: t("Users.expeditionDateLabel"),
        },
        {
          field: "expirationDate",
          header: t("Users.expirationDateLabel"),
        },
        {
          field: "actionsDetail",
          header: "",
        },
        {
          field: "actions",
          header: t("Users.actionsLabel"),
        }
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  };

  const [managementRolesData, setManagementRolesData] = useState<any[]>([]);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [selectedRoles, setSelectedRoles] = useState<any[]>(selectedManagementRoles);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: (col.field === 'select' || col.field === 'editor' || col.field === 'actionsDetail') ? ' ' : t(`Users.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);


  const rowClassName = (data: any) => ((location.pathname === '/users/detail' && data) ? 'p-disabled' : '');

  const onRowEditComplete = (e: any) => {
    let { newData, index } = e;
    const tenantId = newData.tenantId;
    const roleId = newData.id;
    const tenantIndex = managementRolesData.findIndex((tenant: any) => tenant.id === tenantId);
    let tenant: any = managementRolesData[tenantIndex];
    const roleIndex = tenant.roles.findIndex((role: any) => role.id === roleId);

    let expeditionDate = null;
    if (newData.expeditionDate && typeof newData.expeditionDate == 'string') {
      expeditionDate = newData.expeditionDate;
    } else if (newData.expeditionDate) {
      expeditionDate = newData.expeditionDate.toLocaleDateString('en');
    }
    let expirationDate = null;
    if (newData.expirationDate && typeof newData.expirationDate == 'string') {
      expirationDate = newData.expirationDate;
    } else if (newData.expirationDate) {
      expirationDate = newData.expirationDate.toLocaleDateString('en');
    }

    const newDataRol = {
      ...newData,
      expeditionDate,
      expirationDate
    }
    tenant.roles[roleIndex] = newDataRol;
    let newManagementRolesData = [...managementRolesData];
    newManagementRolesData[tenantIndex] = tenant;
    setManagementRolesData(newManagementRolesData);
    const indexRoleSelected = selectedManagementRoles.findIndex((role: any) => role.id == roleId);
    if (indexRoleSelected >= 0) {
      const roleSelected = selectedManagementRoles[indexRoleSelected];
      roleSelected.expeditionDate = expeditionDate;
      roleSelected.expirationDate = expirationDate;

      selectedManagementRoles[indexRoleSelected] = roleSelected;
      setAppState((prev) => ({
        ...prev,
        selectedManagementRoles: selectedManagementRoles
      }));
    }
  };

  const calendarEditor = (options: any) => {
    if (options.field === 'expeditionDate' || options.field === 'expirationDate') {
      let value: any = options.value;
      if (value && typeof value == 'string') {
        value = new Date(value);
      }

      return (
        <Calendar
          label={''}
          value={value}
          onChange={(e) => { options.editorCallback(e.target.value) }}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          className="mt-2 mb-2"
        />
      )
    } else {
      return options.value;
    }
  };



  useMemo(() => {
    if (data) {
      let newData: any = [...data];
      newData = newData.sort((a: any, b: any) => a.name.localeCompare(b.name));
      let indexTenantMain = newData.findIndex((tenant: any) => tenant.id === (selectedTenantId ? selectedTenantId : tenantId));
      if (indexTenantMain >= 0) {
        let main = newData[indexTenantMain];
        newData.splice(indexTenantMain, 1);
        newData.unshift(main);
      }
      let tenantAndRoles = [];
      for (let tenant of newData) {
        let roles = tenant?.roles
          .filter((rol: any) => rol.isAdministrative === true && rol.statusId !== "DELETED")
          .sort((a: any, b: any) => a.name.localeCompare(b.name)
          ).map((role: any) => {
            return { ...role, expeditionDate: null, expirationDate: null }
          });
        if (userRoleData) {
          roles = roles.map((role: any) => {
            const selectedRole = selectedRoles.find((selectedRole: any) => selectedRole.id == role.id);
            if (selectedRole) {
              return {
                ...role,
                expeditionDate: selectedRole.expeditionDate ? (new Date(selectedRole.expeditionDate)).toLocaleDateString('en') : null,
                expirationDate: selectedRole.expirationDate ? (new Date(selectedRole.expirationDate)).toLocaleDateString('en') : null,
              }
            }
            return role;
          })
        }
        tenant.roles = roles;
        tenantAndRoles.push(tenant);
      }
      setManagementRolesData(tenantAndRoles);
    }
  }, [data]);

  return (
    <>
      <div className="mt-4">
        <div>{t("Users.managementRolesDescription")}</div>
      </div>

      {(isButtonAppRoles || isButtonAdminRoles) &&
        <BoxFull className="mt-4 mb-3 px-0 py-0 roles-actions-boxfull" variant="tertiary">
          <div className="subheader-buttons-roles">
            {isButtonAppRoles &&
              <AccessButton
                id='roles-edit-access-button'
                icon="iu iu-icon-edit"
                label={t("Users.goManagementApplication")}
                onClick={() => navigate("/applications")}
              />
            }
            {isButtonAdminRoles &&
              <AccessButton
                id='roles-edit-access-button'
                icon="iu iu-icon-edit"
                label={t("Users.goManagementRoles")}
                onClick={() => navigate("/adminroles")}
              />
            }
          </div>
        </BoxFull>
      }

      <div className="tenant-accordions mt-5">
        {
          managementRolesData.length == 1 &&
          <BoxFull className="p-1">
            <div className={`management-roles-table py-4 px-4 ${location.pathname === '/users/detail' && 'roles-row-disabled'}`}>
              {managementRolesData[0].roles.length > 0 ? (
                <DataTable
                  id={managementRolesData[0].id + '-' + 0}
                  key={managementRolesData[0].id + '-' + 0}
                  className="userpagetabview-roles-datatable"
                  header={
                    <TableHeader
                      title={`${t("Users.managementRolesTable")} 
                    ( ${(selectedManagementRoles &&
                          selectedManagementRoles
                            ?.filter((item: any) => item.tenantId === managementRolesData[0]?.id)?.length) || 0
                        } )
                  `}
                      columns={columns}
                      selectedColumns={selectedColumns}
                      toggleColumns={setSelectedColumns}
                      filters={filters}
                      setFilters={setFilters}
                    //hasCheckColumn
                    />
                  }
                  currentPageReportTemplate={t(
                    "ConsoleTranslations.rowsPaginatorTemplateLabel"
                  )}
                  paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                  emptyMessage={t("TableActions.emptyMessage")}
                  rows={10}
                  size="normal"
                  editMode="row"
                  dataKey="id"
                  sortField="functionality"
                  sortOrder={1}
                  filters={filters}
                  value={managementRolesData[0]?.roles?.map((register: any) => ({
                    ...register,
                    actionsDetail: (
                      <Actions
                        viewButtonEdit={false}
                        viewButtonDelete={false}
                        onDetail={async () => {
                          const { data: dataRolesByTenant } = await getRolesByTenant(register?.tenantId ?? 0);
                          const role = dataRolesByTenant?.find((elem: any) => elem?.id === register?.id);
                          const tenantName = dataTenantsList?.find((elem: any) => elem?.id === register?.tenantId)?.name;
                          setAppState((prev) => ({
                            ...prev,
                            selectedRolesObject: {
                              ...role,
                              tenantName,
                              cameFromRolesPage: true,
                            },
                          }));

                          navigate("/adminroles/detailroles");
                        }}
                      />
                    ),
                  }))}
                  scrollable
                  resizableColumns
                  selectionMode={'checkbox'}
                  selectOnEdit={false}
                  selection={selectedManagementRoles}
                  onSelectionChange={(e) => {
                    onSelectRole(true);

                    setAppState((prev) => ({
                      ...prev,
                      selectedManagementRoles: e.value
                    }))
                  }}
                  rowHover
                  onRowEditComplete={(e) => onRowEditComplete(e)}
                  rowClassName={rowClassName}
                >
                  {selectedColumns.map((col) => (
                    <Column
                      key={col.field}
                      field={col.field}
                      header={col.header}
                      sortable={!(col.field === "actions" || col.field === "select" || col.field === "editor" || col.field === "actionsDetail")}
                      align={col.field === "actions" ? "center" : "left"}
                      headerStyle={{
                        justifyContent: col.field === "actions" ? "center" : undefined,
                        width: col.field === "actionsDetail" ? "60px" : undefined,
                      }}
                      style={{
                        boxShadow: (col.field === "actionsDetail")
                          ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                          : undefined,
                        maxWidth: (col.field === "actions") ? "102px" : (col.field === "editor" || col.field === "actionsDetail") ? '60px' : undefined,
                        whiteSpace: "normal",
                        minWidth: col.field === "select" ? "3.5rem" : (col.field === "actions") ? "102px" :
                          (col.field === "editor" || col.field === "actionsDetail") ? "60px" : "14rem",
                      }}
                      frozen={(col.field === "actionsDetail" || col.field === "actions") ? true : false}
                      alignFrozen={(col.field === "actionsDetail" || col.field === "actions") ? "right" : undefined}
                      selectionMode={col.field === "select" ? 'multiple' : undefined}
                      editor={(options) => calendarEditor(options)}
                      rowEditor={col.field === "actions" ? true : false}
                      resizeable
                    />
                  ))}
                </DataTable>
              ) : (
                <FormSkeleton length={8} />
              )}
            </div>
          </BoxFull>
        }
        {managementRolesData.length > 1 && managementRolesData.map((tenant: any, index) => (
          <Accordion
            activeIndex={1}
            id="accordion"
            items={[
              {
                children: (
                  <div className={`management-roles-table py-4 px-4 ${location.pathname === '/users/detail' && 'roles-row-disabled'}`}>
                    {tenant.roles.length > 0 ? (
                      <DataTable
                        id={tenant.id + '-' + index}
                        key={tenant.id + '-' + index}
                        className="userpagetabview-roles-datatable"
                        header={
                          <TableHeader
                            title={`${t("Users.managementRolesTable")} 
                              ( ${(selectedManagementRoles &&
                                selectedManagementRoles
                                  ?.filter((item: any) => item.tenantId === tenant?.id)?.length) || 0
                              } )
                            `}
                            columns={columns}
                            selectedColumns={selectedColumns}
                            toggleColumns={setSelectedColumns}
                            filters={filters}
                            setFilters={setFilters}
                          //hasCheckColumn
                          />
                        }
                        currentPageReportTemplate={t(
                          "ConsoleTranslations.rowsPaginatorTemplateLabel"
                        )}
                        paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
                        emptyMessage={t("TableActions.emptyMessage")}
                        rows={10}
                        size="normal"
                        editMode="row"
                        dataKey="id"
                        sortField="functionality"
                        sortOrder={1}
                        filters={filters}
                        value={tenant?.roles?.map((register: any) => ({
                          ...register,
                          actionsDetail: (
                            <Actions
                              viewButtonEdit={false}
                              viewButtonDelete={false}
                              onDetail={async () => {
                                const { data: dataRolesByTenant } = await getRolesByTenant(register?.tenantId ?? 0);
                                const role = dataRolesByTenant?.find((elem: any) => elem?.id === register?.id);
                                const tenantName = dataTenantsList?.find((elem: any) => elem?.id === register?.tenantId)?.name;
                                setAppState((prev) => ({
                                  ...prev,
                                  selectedRolesObject: {
                                    ...role,
                                    tenantName,
                                    cameFromRolesPage: true,
                                  },
                                }));

                                navigate("/adminroles/detailroles");
                              }}
                            />
                          ),
                        }))}
                        scrollable
                        resizableColumns
                        selectionMode={'checkbox'}
                        selectOnEdit={false}
                        selection={selectedManagementRoles}
                        onSelectionChange={(e) => {
                          onSelectRole(true);
                          if (e.type == 'all') {
                            const roleIds = tenant.roles.map((role: any) => role.id);
                            let currentSelectedManagementRoles: any[] = [];
                            if (e.value.length == 0) {
                              currentSelectedManagementRoles = selectedManagementRoles.filter((selectedRole: any) => !roleIds.includes(selectedRole.id));
                            } else {
                              currentSelectedManagementRoles = [...selectedManagementRoles, ...e.value];
                            }
                            currentSelectedManagementRoles = Object.values(
                              currentSelectedManagementRoles.reduce((acc, obj) => ({ ...acc, [obj.id]: obj }), {})
                            )
                            setAppState((prev) => ({
                              ...prev,
                              selectedManagementRoles: currentSelectedManagementRoles
                            }))
                          } else {
                            setAppState((prev) => ({
                              ...prev,
                              selectedManagementRoles: e.value
                            }))
                          }
                        }}
                        rowHover
                        onRowEditComplete={(e) => onRowEditComplete(e)}
                        rowClassName={rowClassName}
                      >
                        {selectedColumns.map((col) => (
                          <Column
                            key={col.field}
                            field={col.field}
                            header={col.header}
                            sortable={!(col.field === "actions" || col.field === "select" || col.field === "editor" || col.field === "actionsDetail")}
                            align={col.field === "actions" ? "center" : "left"}
                            headerStyle={{
                              justifyContent: col.field === "actions" ? "center" : undefined,
                              width: col.field === "actionsDetail" ? "60px" : undefined,
                            }}
                            style={{
                              boxShadow: (col.field === "actionsDetail")
                                ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                                : undefined,
                              maxWidth: (col.field === "actions") ? "102px" : (col.field === "editor" || col.field === "actionsDetail") ? '60px' : undefined,
                              whiteSpace: "normal",
                              minWidth: col.field === "select" ? "3.5rem" : (col.field === "actions") ? "102px" :
                                (col.field === "editor" || col.field === "actionsDetail") ? "60px" : "14rem",
                            }}
                            frozen={(col.field === "actionsDetail") ? true : false}
                            alignFrozen={col.field === "actions" ? "right" : undefined}
                            selectionMode={col.field === "select" ? 'multiple' : undefined}
                            editor={(options) => calendarEditor(options)}
                            rowEditor={col.field === "actions" ? true : false}
                            resizeable
                          />
                        ))}
                      </DataTable>
                    ) : (
                      <FormSkeleton length={8} />
                    )}
                  </div>
                ),
                disabled: false,
                header: tenant?.name,
                key: tenant?.id,
              },
            ]}
          />
        ))}
      </div>

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
